import { logError } from '@/lib/logger';

interface PreviewOperation {
  operationName: string | undefined;
  variables: Record<string, unknown>;
  getContext: () => Record<string, unknown>;
}

interface SubmittedFilter {
  field: string;
  operator: string;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Read only submitted enum names; never copy filter values or full variables. */
function getSubmittedFilters(groups: unknown): SubmittedFilter[] {
  if (!Array.isArray(groups)) return [];

  return groups.flatMap((group) => {
    const record = asRecord(group);
    if (!record) return [];
    const filters = Array.isArray(record.filters)
      ? record.filters.flatMap((filter) => {
          const row = asRecord(filter);
          return typeof row?.field === 'string' &&
            typeof row.operator === 'string'
            ? [{ field: row.field, operator: row.operator }]
            : [];
        })
      : [];
    return [...filters, ...getSubmittedFilters(record.groups)];
  });
}

/** Explicit preview metadata is captured when the request is sent. */
export function getPreviewErrorContext(operation: PreviewOperation) {
  const preview = asRecord(operation.getContext().publicSearchPreview);
  if (!preview) return undefined;

  const request = asRecord(operation.variables.requestInput);
  return {
    purpose: 'preview',
    operationName: operation.operationName,
    societyCode:
      typeof preview.societyCode === 'string' ? preview.societyCode : undefined,
    searchType:
      typeof preview.searchType === 'string' ? preview.searchType : undefined,
    submittedFilters: getSubmittedFilters(request?.filterGroups),
  };
}

export function getPreviewGraphQLErrorDetails(error: unknown) {
  const record = asRecord(error);
  const extensions = asRecord(record?.extensions);
  return {
    errorCode:
      typeof extensions?.code === 'string' ? extensions.code : undefined,
    errorPath: Array.isArray(record?.path)
      ? record.path.filter(
          (part) => typeof part === 'string' || typeof part === 'number',
        )
      : undefined,
  };
}

/** HTTP 400 GraphQL validation failures may arrive as Apollo network errors. */
export function getPreviewNetworkErrorDetails(error: {
  statusCode?: number;
  bodyText?: string;
}) {
  let graphQLErrors;
  try {
    const body = asRecord(JSON.parse(error.bodyText ?? ''));
    if (Array.isArray(body?.errors)) {
      graphQLErrors = body.errors.map(getPreviewGraphQLErrorDetails);
    }
  } catch {
    // Non-GraphQL response bodies are deliberately omitted.
  }
  return { statusCode: error.statusCode, graphQLErrors };
}

/** This failure precedes Apollo, so the shared error link cannot report it. */
export function logUnmappedPreviewLookup(context: {
  societyCode?: string | null;
  searchType: string;
  operationName: string;
  field: string;
  operator?: string;
}) {
  logError('[GraphQL preview lookup error]', undefined, {
    purpose: 'preview',
    societyCode: context.societyCode,
    searchType: context.searchType,
    operationName: context.operationName,
    submittedFilters: [{ field: context.field, operator: context.operator }],
    errorCode: 'UNMAPPED_LOOKUP',
  });
}
